import { useState } from 'react'
import { useReceiveData } from '../hooks/useReceiveData.js'
import { formatMoney, formatQty } from '../lib/format.js'
import AnomalyScanModal from './AnomalyScanModal.jsx'
import InvoiceScanModal from './InvoiceScanModal.jsx'
import ReceiveEntryForm from './ReceiveEntryForm.jsx'
import ReceivePhotoThumb from './ReceivePhotoThumb.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import Toast from './Toast.jsx'

// Receive Inventory screen (SOP 2).
// Layout:
//   - Today's deliveries summary card ($ total + supplier count)
//   - Recent deliveries list with delete button per row
//   - Sticky "Log Delivery" CTA at bottom
//
// Mirrors the Waste Tracking screen pattern so the manager has
// consistent muscle memory between the two daily flows.
export default function ReceiveInventoryScreen() {
  const {
    items,
    suppliers,
    todayLogs,
    todayTotal,
    todaySupplierCount,
    recentReceives,
    loading,
    refresh,
    deleteLog,
  } = useReceiveData()

  const [formOpen, setFormOpen] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  const [anomalyOpen, setAnomalyOpen] = useState(false)
  const [toast, setToast] = useState('')
  const [deletingId, setDeletingId] = useState(null)

  async function handleLogged({ total }) {
    await refresh()
    setFormOpen(false)
    setToast(
      `Logged ${formatMoney(total)} delivery. Today's deliveries: ${formatMoney(
        Number(todayTotal) + Number(total)
      )}.`
    )
  }

  async function handleScanLogged(result) {
    // Result shape: { deliveries, newItems }
    const { deliveries, newItems } = result || {}
    await refresh()
    setScanOpen(false)
    const dPart = `Logged ${deliveries} ${deliveries === 1 ? 'delivery' : 'deliveries'} from invoice`
    const iPart = newItems > 0
      ? ` \u00b7 added ${newItems} new ${newItems === 1 ? 'item' : 'items'} to your catalog`
      : ''
    setToast(dPart + iPart + '.')
  }

  async function handleDeleteLog(log) {
    if (deletingId) return
    const ok = window.confirm(
      `Remove this delivery?\n\n${log.item_name} · ${log.supplier_name} — ${formatMoney(log.total_cost)}`
    )
    if (!ok) return
    setDeletingId(log.id)
    try {
      await deleteLog(log)
      setToast(`Removed ${formatMoney(log.total_cost)} delivery.`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleDeleteLog]', e)
      setToast('Could not remove delivery. Try again.')
    } finally {
      setDeletingId(null)
    }
  }

  // Friendly relative time so the row reads "10:42 AM" not the full ISO.
  const fmtTime = (iso) => {
    if (!iso) return ''
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // First-paint skeleton — keeps the layout stable while suppliers
  // and today's deliveries are fetching.
  if (loading && todayLogs.length === 0 && suppliers.length === 0) {
    return (
      <div className="min-h-full bg-white pb-28">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-40" />
            <SkeletonList rows={3} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white pb-28">
      <main className="mx-auto max-w-screen-sm">
        {/* Today's deliveries summary */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Today's deliveries"
        >
          <div className="text-xs font-medium text-black/60">
            Today's Deliveries
          </div>
          <div className="mt-2 text-5xl font-medium tabular-nums text-black">
            {formatMoney(todayTotal)}
          </div>
          <div className="mt-2 text-sm text-black/60">
            {todayLogs.length === 0 ? (
              <>No deliveries logged today.</>
            ) : (
              <>
                {todayLogs.length} {todayLogs.length === 1 ? 'delivery' : 'deliveries'} from{' '}
                {todaySupplierCount}{' '}
                {todaySupplierCount === 1 ? 'supplier' : 'suppliers'}
              </>
            )}
          </div>

          {/* AI Anomaly scan — checks the last 90 days of receives for
              price hikes, fat-fingered quantities, supplier swaps. */}
          <button
            type="button"
            onClick={() => setAnomalyOpen(true)}
            disabled={(recentReceives?.length ?? 0) === 0}
            className="mt-3 min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            ✨ Check invoices for anomalies
          </button>
          <p className="mt-1 text-xs text-black/55 leading-snug">
            Scans the last 90 days of receives for supplier price hikes,
            unusual quantities, and other red flags. Flagged items show
            severity + reasoning + suggested action.
          </p>
        </section>

        {/* Recent deliveries */}
        <section className="mx-4 mt-6" aria-label="Recent deliveries">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-medium">Recent deliveries</h2>
            <span className="text-xs text-black/60">
              {todayLogs.length} today
            </span>
          </div>

          {todayLogs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              No deliveries logged yet today. Tap{' '}
              <span className="font-medium text-blue-600">Log Delivery</span>{' '}
              when one arrives.
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {todayLogs.slice(0, 12).map((l) => {
                const isDeleting = deletingId === l.id
                return (
                  <li
                    key={l.id}
                    className={`flex items-center gap-3 border-b border-black/5 px-4 py-3 last:border-b-0 ${
                      isDeleting ? 'opacity-50' : ''
                    }`}
                  >
                    {/* Photo thumbnail — photo_url holds a private-bucket
                        object path; ReceivePhotoThumb exchanges it for a
                        short-TTL signed URL before rendering. */}
                    <ReceivePhotoThumb
                      photoPath={l.photo_url}
                      alt={`${l.item_name} delivery`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium">
                        {l.item_name}
                      </div>
                      <div className="truncate text-xs text-black/60">
                        {formatQty(l.quantity, l.unit)} · {l.supplier_name}
                        {l.notes ? ` · ${l.notes}` : ''}
                      </div>
                      <div className="text-[11px] text-black/45">
                        {fmtTime(l.logged_at)}
                      </div>
                    </div>
                    <div className="text-right text-base font-medium tabular-nums">
                      {formatMoney(l.total_cost)}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteLog(l)}
                      disabled={isDeleting}
                      aria-label={`Remove ${l.item_name} delivery`}
                      title="Remove delivery (logged by accident)"
                      className="min-h-btn min-w-[44px] rounded-lg px-2 text-base text-black/40 hover:bg-red-50 hover:text-red-600 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
                    >
                      {isDeleting ? '…' : '✕'}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </main>

      {/* Sticky primary actions — Scan invoice (AI) + Log Delivery (manual) */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-black/5 bg-white/95 backdrop-blur safe-bottom">
        <div className="mx-auto max-w-screen-sm px-4 pt-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setScanOpen(true)}
              className="min-h-btn rounded-lg border-2 border-blue-600 bg-white px-3 py-4 text-base font-medium text-blue-700 shadow-sm hover:bg-blue-50 active:bg-blue-100"
            >
              Scan Invoice
            </button>
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="min-h-btn rounded-lg bg-blue-600 px-3 py-4 text-base font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800"
            >
              Log Delivery
            </button>
          </div>
          <p className="mt-1.5 text-center text-[11px] text-black/55">
            Scan = AI extracts a multi-item invoice · Log = type one delivery
          </p>
        </div>
      </div>

      <ReceiveEntryForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onLogged={handleLogged}
        items={items}
        suppliers={suppliers}
      />

      <AnomalyScanModal
        open={anomalyOpen}
        onClose={() => setAnomalyOpen(false)}
        receiveLogs={recentReceives}
        windowDays={90}
      />

      <InvoiceScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onLogged={handleScanLogged}
        items={items}
        suppliers={suppliers}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}
